/** Mirrors the API's NotificationEntityAction; kept alias-free for Node tests. */
type EntityAction =
  | 'JobCreated'
  | 'SourcingStarted'
  | 'SourcingFailed'
  | 'SourcingFinished'
  | 'ManualUploadFinished'
  | 'ManualUploadFailed'
  | 'NewMessage'
  | 'OutreachGenerated'
  | 'JobImportSuccess'
  | 'JobImportFailed'
  | 'MessageReviewRequired';

const jobCandidates = '/hm/jobs/{id}/candidates';

/** TENANT_QUERY_PARAM from hiring-manager/utils/tenant.ts, which Node tests cannot import. */
const tenantParam = 'tenantId';

/**
 * Where a notification takes a portal user. `{id}` is the notification's
 * entity id: a job id for job and outreach events, a thread id for messages
 * (the Outreach page has no thread deep link yet).
 */
const portalLinks: Record<EntityAction, string> = {
  JobCreated: jobCandidates,
  SourcingStarted: jobCandidates,
  SourcingFailed: jobCandidates,
  SourcingFinished: jobCandidates,
  ManualUploadFinished: jobCandidates,
  ManualUploadFailed: jobCandidates,
  // `review=drafts` opens the drafts sheet (ManualOutreachView.tsx).
  OutreachGenerated: `${jobCandidates}?status=inProgress&review=drafts`,
  NewMessage: '/hm/messages',
  MessageReviewRequired: '/hm/messages',
  JobImportSuccess: '/hm',
  JobImportFailed: '/hm',
};

/**
 * The portal link for a notification, keeping only the selected company from
 * the current query string: page filters such as `?status=passed` belong to
 * the page the bell was opened on, not the destination. Undefined when the
 * notification points nowhere or its link needs an id it lacks.
 */
export function portalNotificationLink(
  notification: {
    entityAction?: EntityAction | null;
    entityId?: string | null;
  },
  currentSearch: URLSearchParams,
): string | undefined {
  if (!notification.entityAction) return undefined;
  const template = portalLinks[notification.entityAction];
  if (template.includes('{id}') && !notification.entityId) return undefined;
  const [path, query = ''] = template
    .replace('{id}', notification.entityId ?? '')
    .split('?');
  const search = new URLSearchParams();
  const tenantId = currentSearch.get(tenantParam);
  if (tenantId) search.set(tenantParam, tenantId);
  new URLSearchParams(query).forEach((value, key) => search.set(key, value));
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
}
